import * as React from 'react';
import { Copy } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { useClipboard } from 'use-clipboard-copy';
import { notifySuccess } from '@gandi/notifications';
import { useTranslator } from '@gandi/translate';
import { Block, BlockHeading, Button, HeadingIcon, Text, Base } from '@gandi/ui';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { ListBlock } from './ListBlock';
import { NextStepHeader } from './NextStepHeader';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var styles = {
  "root": "OfflinePayment-root_mESIE",
  "infoRow": "OfflinePayment-infoRow_1bOtQ",
  "label": "OfflinePayment-label_3wne8",
  "content": "OfflinePayment-content_3BKGW"
};
const displayBlock = {
  display: 'block'
};
function extractBankValues(bu) {
  return {
    buName: bu.address.name || bu.name,
    bankName: bu.bank.name,
    bankStreet: bu.bank.address.street,
    bankZipAndCity: bu.bank.address.zip ? `${bu.bank.address.zip} ${bu.bank.address.city}` : bu.bank.address.city,
    bankCountry: bu.bank.address.country_name,
    bankAccount: bu.bank.account || '-',
    bankIBAN: bu.bank.iban || '-',
    bankBIC: bu.bank.bic || '-',
    bankEmail: bu.email,
    bankFax: bu.fax
  };
}
function getTransferInfos(bu, __, id) {
  if (!bu.bank) {
    return [];
  }
  const bankValues = extractBankValues(bu);
  return [{
    label: __('Bank Address'),
    values: [bankValues.bankName, bankValues.bankStreet, bankValues.bankZipAndCity, bankValues.bankCountry]
  }, {
    label: __('Account holder'),
    values: [bankValues.buName, bankValues.bankAccount]
  }, {
    label: __('IBAN'),
    values: [bankValues.bankIBAN]
  }, {
    label: __('BIC'),
    values: [bankValues.bankBIC]
  }, {
    label: __('Comment field'),
    values: [__('Order %(id)s', {
      id
    })]
  }];
}
function getCheckInfos(bu) {
  return [{
    values: [bu.address.name || bu.name, bu.address.street, bu.address.street2 || '', `${bu.address.zip || ''} ${bu.address.city || ''}`, bu.address.country_name]
  }];
}
function getMandateInfos(bu, __) {
  return [{
    label: __('Email'),
    values: [bu.email]
  }, {
    label: __('Mail'),
    values: [bu.address.name || bu.name, bu.address.street, bu.address.street2 || '', `${bu.address.zip || ''} ${bu.address.city || ''}`, bu.address.country_name]
  }, {
    label: __('Fax'),
    values: [bu.fax]
  }];
}
function usePaymentTypeTranslation({
  payment_type,
  bu,
  id
}) {
  const {
    __
  } = useTranslator();
  switch (payment_type) {
    case 'transfer':
      return {
        title: __('Where to address your bank transfer.'),
        content: getTransferInfos(bu, __, id)
      };
    case 'check':
      return {
        title: __('Where to send your check purchase.'),
        content: getCheckInfos(bu)
      };
    case 'mandate':
      return {
        title: __('Where to send your purchase order.'),
        content: getMandateInfos(bu, __)
      };
    default:
      return undefined;
  }
}
const InfoItem = value => /*#__PURE__*/_jsx("span", {
  style: displayBlock,
  children: value
}, `destination-info-item-${value}`);
const InfoRow = (info, idx) => {
  const dispatch = useDispatch();
  const {
    __
  } = useTranslator();
  const FFShopConfirmationNDS = useFeatureFlag('FF_SHOP_CONFIRMATION_NDS');
  const values = info.values.filter(Boolean);
  const items = values.map(InfoItem);
  const {
    copy
  } = useClipboard({
    copiedTimeout: 500,
    onSuccess() {
      dispatch(notifySuccess(__('Successfully copied')));
    }
  });
  const text = values.join('\n');
  function onCopy() {
    copy(text);
  }
  if (FFShopConfirmationNDS) {
    return /*#__PURE__*/_jsxs("div", {
      className: styles.infoRow,
      children: [/*#__PURE__*/_jsx(Text, {
        weight: "semibold",
        className: styles.label,
        children: info.label
      }), /*#__PURE__*/_jsx("div", {
        className: styles.content,
        children: items
      }), text && /*#__PURE__*/_jsx(Button, {
        type: "button",
        kind: "ghost",
        onClick: onCopy,
        children: /*#__PURE__*/_jsx(Copy, {
          size: 16
        })
      })]
    }, info.label);
  }
  return /*#__PURE__*/_jsxs(Base, {
    flex: true,
    flexJustifyBetween: true,
    flexWrap: true,
    gap: "xsmall",
    marginBottom: "small",
    children: [/*#__PURE__*/_jsx(Text, {
      children: info.label
    }), /*#__PURE__*/_jsx(Base, {
      tagName: "p",
      weight: "semibold",
      align: "end",
      margin: "none",
      children: items
    })]
  }, idx);
};
export default function OfflinePayment({
  order
}) {
  const translator = useTranslator();
  const FFShopConfirmationNDS = useFeatureFlag('FF_SHOP_CONFIRMATION_NDS');
  const paymentTypeTranslation = usePaymentTypeTranslation(order);
  if (!paymentTypeTranslation) {
    return null;
  }
  if (FFShopConfirmationNDS) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(NextStepHeader, {
        content: translator.__('Specify the order number in the transfer’s comment field.')
      }), /*#__PURE__*/_jsx(ListBlock, {
        className: styles.root,
        children: paymentTypeTranslation.content.map(InfoRow)
      })]
    });
  }
  return /*#__PURE__*/_jsxs(Block, {
    children: [/*#__PURE__*/_jsxs(BlockHeading, {
      fontSize: "m",
      children: [/*#__PURE__*/_jsx(HeadingIcon, {
        name: "arrowRight"
      }), paymentTypeTranslation.title]
    }), paymentTypeTranslation.content.map(InfoRow)]
  });
}